import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MKBox from 'components/MaterialKit/MKBox';
import { getErrorMessage, handleErrorResponse } from 'utils/general';
import { withLoading } from 'utils/hoc';
import { useAuth } from 'contexts/auth';
import { getLocaleMap } from 'utils/locales';
import { createMonitorItem, deleteMonitorItem, getModels, getMonitorItem, updateMonitorItem, updateUserProducts } from 'api/monitors';
import EditMonitorForm from './EditMonitorForm';

const EditMonitorSection = ({
  title_color,
  title_font_size,
}) => {
  const [lm, setLm] = useState({});

  useEffect(() => {
    getLocaleMap([])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const [monitorItem, setMonitorItem] = useState(null);
  const [models, setModels] = useState(null);
  const [searchParams] = useSearchParams();
  const { auth, setAuth } = useAuth();
  const navigate = useNavigate();
  const monitorItemId = searchParams.get('monitor_item_id');

  const fetchMonitorItemFromApi = useCallback(() => {
    if (!monitorItemId) {
      return Promise.resolve();
    }
    // setLoading(true);
    return getMonitorItem(monitorItemId, { $expand: 'model/brand,model/default_image' })
      .then(({ data }) => {
        setMonitorItem(data);
      })
      .catch((error) => {
        handleErrorResponse(error, setAuth);
      })
      .finally(() => {
        // setLoading(false);
      });
  }, [monitorItemId, setAuth]);

  const fetchModelsFromApi = useCallback(() => {
    // setLoading(true);
    return getModels({ $expand: 'default_image', $orderby: 'id_name' })
      .then(({ data }) => {
        setModels(data);
      })
      .catch((error) => {
        handleErrorResponse(error, setAuth);
      })
      .finally(() => {
        // setLoading(false);
      });
  }, [setAuth]);

  const updateMonitorItemToApi = useCallback((values, { setFieldError }) => {
    if (!monitorItemId) {
      return Promise.resolve();
    }
    const updateBody = {
      model: values.model,
      condition: values.condition,
      monitor_type: values.monitor_type,
      currency: values.currency,
      expected_lowest_price: values.expected_lowest_price ? values.expected_lowest_price : null,
      expected_highest_price: values.expected_highest_price ? values.expected_highest_price : null,
      source_account: values.source_account ? values.source_account : null,
    };
    // setLoading(true);
    return updateMonitorItem(monitorItemId, updateBody)
      .then(() => {
        return updateUserProducts().then(() => {
          navigate(-1);
        });
      })
      .catch((err) => {
        setFieldError('form', err.message);
      })
      .finally(() => {
        // setLoading(false);
      });
  }, [monitorItemId, navigate]);

  const createMonitorItemToApi = useCallback((values, { setFieldError }) => {
    const createBody = {
      model: values.model,
      condition: values.condition,
      monitor_type: values.monitor_type,
      currency: values.currency,
      expected_lowest_price: values.expected_lowest_price ? values.expected_lowest_price : null,
      expected_highest_price: values.expected_highest_price ? values.expected_highest_price : null,
      source_account: values.source_account ? values.source_account : null,
      user: auth?.user?.username,
    };
    // setLoading(true);
    return createMonitorItem(createBody)
      .then(() => {
        navigate(-1);
      })
      .catch((err) => {
        setFieldError('form', getErrorMessage(err));
      })
      .finally(() => {
        // setLoading(false);
      });
  }, [auth?.user?.username, navigate]);

  const deleteMonitorItemToApi = useCallback((setFieldError) => {
    // setLoading(true);
    return deleteMonitorItem(monitorItemId)
      .then(() => {
        navigate(-1);
      })
      .catch((err) => {
        setFieldError('form', getErrorMessage(err));
      })
      .finally(() => {
        // setLoading(false);
      });
  }, [monitorItemId, navigate]);

  useEffect(() => {
    fetchMonitorItemFromApi();
    fetchModelsFromApi();
  }, [fetchMonitorItemFromApi, fetchModelsFromApi]);

  return (
    <MKBox width="100%" padding="0.5rem 1rem">
      <EditMonitorForm
        monitorItem={monitorItem}
        models={models}
        onSave={monitorItemId ? updateMonitorItemToApi : createMonitorItemToApi}
        onDelete={deleteMonitorItemToApi}
        titleColor={title_color}
        titleFontSize={title_font_size}
      />
    </MKBox>
  );
};

EditMonitorSection.propTypes = {
  // setLoading: PropTypes.func,
  title_color: PropTypes.string,
  title_font_size: PropTypes.string,
};

export default EditMonitorSection;
